// M32 — FeedbackProvider. Owns the "is the dialog open" state and the
// initial values fed into it. Mounts the dialog once at the top of the
// app tree so any component (FeedbackButton in the shell, the
// ErrorFallback's "Send feedback" link, the footer link) can call
// `useFeedback().open()` and have the same modal pop up.
//
// Splitting the hook/context into useFeedback.ts and keeping this file
// as a component-only export satisfies the react-refresh rule that
// flagged AuthContext in M16b.

import { useCallback, useState, type ReactNode } from 'react';
import FeedbackDialog from './FeedbackDialog';
import { FeedbackContext, type OpenFeedbackOptions } from './useFeedback';

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  // Cached so the dialog can read pre-fill kind/message/errorContext on
  // its next render. We don't replace state per-call to avoid an extra
  // render between "open requested" and "dialog opens with options".
  const [options, setOptions] = useState<OpenFeedbackOptions>({});

  const openDialog = useCallback((opts?: OpenFeedbackOptions) => {
    setOptions(opts ?? {});
    setOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setOpen(false);
  }, []);

  return (
    <FeedbackContext.Provider value={{ open: openDialog }}>
      {children}
      <FeedbackDialog open={open} initialOptions={options} onClose={closeDialog} />
    </FeedbackContext.Provider>
  );
}
